import * as angular from 'angular';
import * as _ from 'lodash';

import { ITranslatedLoggerService, IDataFolderService } from '../../services';
import { ComponentDefinition, FileInformation, ConnectionPoint, DirectoryInformation, DataFolderDto } from '../../models';

const supportedExtensions = ['.xml', '.csv', '.pdf', '.png', '.jpg', '.jpeg'];

class DataFolderDirectoryTreeController implements angular.IComponentController {
    isLoading: boolean;
    isExpanded: boolean = false;

    readonly connectionPointGuid: string;
    readonly connectionPoint: ConnectionPoint;
    readonly directory: DirectoryInformation;
    readonly level: number;
    readonly showRestoreButton: boolean;
    readonly relativePath: string;
    readonly onFileSelected: (args: { guid: string; file: FileInformation }) => void;
    readonly refresh: () => void;

    static $inject = ['$scope', 'loggerService', 'dataFolderService'];

    constructor(private _$scope: angular.IScope, private _loggerService: ITranslatedLoggerService, private _dataFolderService: IDataFolderService) {}

    $onInit() {
        this.isExpanded = (this.level || 0) === 0;
    }

    shouldShowRestoreForThisDirectory() {
        const dirName = this.directory?.name?.toLowerCase();
        return dirName === 'processed' || dirName === 'deleted' || dirName === 'error';
    }

    toggleExpanded(): void {
        this.isExpanded = !this.isExpanded;
    }

    isSupported(file: FileInformation) {
        const n = file.name.toLowerCase();
        return !!this.connectionPoint?.scriptFile ? true : _.some(supportedExtensions, (d) => n.endsWith(d));
    }

    humanFileSize(size: number) {
        if (!size) {
            return '';
        }
        const i = Math.floor(Math.log(size) / Math.log(1024));
        return (size / Math.pow(1024, i)).toFixed(2) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
    }

    download(file: FileInformation) {
        if (file && this.connectionPointGuid) {
            const relativePath = this.buildRelativePath(file.name);
            this._dataFolderService.download(this.connectionPointGuid, relativePath);
        }
    }

    private buildRelativePath(fileName: string) {
        if (this.relativePath) {
            return `${this.relativePath}/${fileName}`;
        } else {
            if (this.directory?.name) {
                return `${this.directory.name}/${fileName}`;
            } else {
                return fileName;
            }
        }
    }

    upload(file: FileInformation) {
        if (file && this.connectionPointGuid) {
            this.isLoading = true;
            this._dataFolderService
                .process(this.connectionPointGuid, file.name)
                .then((data) => {
                    if (data.errors?.length) {
                        this._loggerService.logError(data.errors.map((d) => d.value).join(','));
                    }
                    const tagGuids = data.imported ? Object.keys(data.imported) : [];
                    const count = _.sumBy(tagGuids, (g) => data.imported[g]);
                    const spotTagGuids = data.importedSpot ? Object.keys(data.importedSpot) : [];
                    const spotCount = _.sumBy(spotTagGuids, (g) => data.importedSpot[g]);
                    if (count + spotCount > 0 || tagGuids.length + spotTagGuids.length > 0) {
                        this._loggerService.logSuccess(`Imported ${count + spotCount} values for ${tagGuids.length + spotTagGuids.length} tags.`);
                    } else {
                        this._loggerService.log(`No data imported.`);
                    }
                    if (data.reprocessed > 0) {
                        this._loggerService.logSuccess(`${data.reprocessed} new reprocessing tasks launched.`, true);
                    }
                    this.onRefresh();
                })
                .catch((reason) => this._loggerService.logError(reason))
                .finally(() => (this.isLoading = false));
        }
    }

    delete(file: FileInformation) {
        if (file && this.connectionPointGuid) {
            this.isLoading = true;
            const relativePath = this.buildRelativePath(file.name);
            const dataFolderDto: DataFolderDto = { fileName: relativePath };
            this._dataFolderService
                .delete(this.connectionPointGuid, dataFolderDto)
                .then(() => {
                    this.onRefresh();
                })
                .catch((reason) => this._loggerService.logError(reason))
                .finally(() => (this.isLoading = false));
        }
    }

    restore(file: FileInformation) {
        if (file && this.connectionPointGuid) {
            this.isLoading = true;
            const relativePath = this.buildRelativePath(file.name);
            const dataFolderDto: DataFolderDto = { fileName: relativePath };
            this._dataFolderService
                .restore(this.connectionPointGuid, dataFolderDto)
                .then(() => {
                    this.onRefresh();
                })
                .catch((reason) => this._loggerService.logError(reason))
                .finally(() => (this.isLoading = false));
        }
    }

    onItemClick(file: FileInformation) {
        if (this.onFileSelected) {
            const fileWithPath: FileInformation = {
                ...file,
                name: this.buildRelativePath(file.name)
            };

            this.onFileSelected({ guid: this.connectionPointGuid, file: fileWithPath });
        }
    }

    handleFileSelection(guid: string, file: FileInformation) {
        if (this.onFileSelected) {
            this.onFileSelected({ guid: guid, file: file });
        }
    }

    onRefresh() {
        if (this.refresh) {
            this.refresh();
        }
    }
}

export const wtDataFolderDirectoryTree: ComponentDefinition = {
    componentId: 'wtDataFolderDirectoryTree',
    controller: DataFolderDirectoryTreeController,
    controllerAs: 'vm',
    template: require('./dataFolderDirectoryTree.component.html'),
    bindings: {
        connectionPointGuid: '<',
        connectionPoint: '<',
        directory: '<',
        level: '<',
        showRestoreButton: '<',
        relativePath: '<',
        onFileSelected: '&',
        refresh: '&'
    }
};
